import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { orderService, Order, CreateOrderRequest, UpdateOrderStatusRequest } from '@/services/orderService';

/**
 * Order State Interface
 */
interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;
}

/**
 * Initial state
 */
const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  isLoading: false,
  error: null,
  pagination: null,
};

/**
 * Create order from cart
 */
export const createOrder = createAsyncThunk<
  Order,
  CreateOrderRequest,
  { rejectValue: string }
>('order/createOrder', async (orderData, { rejectWithValue }) => {
  try {
    const response = await orderService.createOrder(orderData);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create order'
    );
  }
});

/**
 * Fetch user orders
 */
export const fetchOrders = createAsyncThunk<
  { orders: Order[]; pagination: any },
  any,
  { rejectValue: string }
>('order/fetchOrders', async (filters, { rejectWithValue }) => {
  try {
    const response = await orderService.getOrders(filters);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch orders'
    );
  }
});

/**
 * Fetch order by ID
 */
export const fetchOrderById = createAsyncThunk<
  Order,
  string,
  { rejectValue: string }
>('order/fetchOrderById', async (orderId, { rejectWithValue }) => {
  try {
    const response = await orderService.getOrderById(orderId);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch order'
    );
  }
});

/**
 * Update order status
 */
export const updateOrderStatus = createAsyncThunk<
  Order,
  { orderId: string; statusData: UpdateOrderStatusRequest },
  { rejectValue: string }
>('order/updateStatus', async ({ orderId, statusData }, { rejectWithValue }) => {
  try {
    const response = await orderService.updateOrderStatus(orderId, statusData);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update order status'
    );
  }
});

/**
 * Cancel order
 */
export const cancelOrder = createAsyncThunk<
  Order,
  { orderId: string; reason?: string },
  { rejectValue: string }
>('order/cancelOrder', async ({ orderId, reason }, { rejectWithValue }) => {
  try {
    const response = await orderService.cancelOrder(orderId, reason ? { reason } : undefined);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to cancel order'
    );
  }
});

/**
 * Fetch vendor orders
 */
export const fetchVendorOrders = createAsyncThunk<
  { orders: Order[]; pagination: any },
  any,
  { rejectValue: string }
>('order/fetchVendorOrders', async (filters, { rejectWithValue }) => {
  try {
    const response = await orderService.getVendorOrders(filters);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch vendor orders'
    );
  }
});

/**
 * Accept a broadcast order (vendor takes the order)
 */
export const acceptOrder = createAsyncThunk<
  Order,
  string,
  { rejectValue: string }
>('order/acceptOrder', async (orderId, { rejectWithValue }) => {
  try {
    const response = await orderService.acceptOrder(orderId);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to accept order'
    );
  }
});

/**
 * Order slice
 */
const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetOrders: (state) => {
      state.orders = [];
      state.currentOrder = null;
      state.pagination = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create order
    builder
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        state.orders = [action.payload, ...state.orders];
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create order';
      });

    // Fetch orders
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch orders';
      });

    // Fetch order by ID
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch order';
      });

    // Update order status
    builder
      .addCase(updateOrderStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update order status';
      });

    // Cancel order
    builder
      .addCase(cancelOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to cancel order';
      });

    // Fetch vendor orders
    builder
      .addCase(fetchVendorOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchVendorOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch vendor orders';
      });

    // Accept order (broadcast → accepted)
    builder
      .addCase(acceptOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(acceptOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to accept order';
      });
  },
});

// Export actions
export const { setCurrentOrder, clearError, resetOrders } = orderSlice.actions;

// Export selectors
export const selectOrders = (state: { order: OrderState }) => state.order.orders;
export const selectCurrentOrder = (state: { order: OrderState }) => state.order.currentOrder;
export const selectOrderLoading = (state: { order: OrderState }) => state.order.isLoading;
export const selectOrderError = (state: { order: OrderState }) => state.order.error;
export const selectOrderPagination = (state: { order: OrderState }) => state.order.pagination;

export default orderSlice.reducer;




